import React from "react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { EmbeddableChatWidget } from "../../components/chat/embeddable-chat-widget";

export default function PublicChatWidgetPage() {
  const [params] = useSearchParams();
  const [isClient, setIsClient] = useState(false);

  // Ensure client-side rendering to prevent hydration mismatches
  useEffect(() => {
    setIsClient(true);
  }, []);

  // Legacy params mapping
  const tracking = params.get("ref") || params.get("tracking") || undefined;
  const assetId = params.get("asset") || undefined;
  const status = params.get("status") || undefined;
  const error = params.get("error") || undefined;

  // Newer params used by conversation-aware redirects
  const conversationId = params.get("conversation") || undefined;
  const startMessage = params.get("startMessage") || undefined;
  const deviceParam = (params.get("device") || "DESKTOP").toUpperCase();
  const allowedDevices = new Set([
    "PHONE",
    "MOBILE",
    "DESKTOP",
    "TABLET",
    "OTHER",
  ]);
  const device = (allowedDevices.has(deviceParam) ? deviceParam : "DESKTOP") as
    | "PHONE"
    | "MOBILE"
    | "DESKTOP"
    | "TABLET"
    | "OTHER";
  const [prefill, setPrefill] = useState<string | undefined>(undefined);
  useEffect(() => {
    (async () => {
      try {
        if (startMessage) {
          setPrefill(startMessage);
        }
      } catch (e) {
        console.warn("Unable to set prefill:", e);
      }
    })();
  }, [conversationId, startMessage]);

  // Optionally display a lightweight status/error notice above the widget
  const Banner = () => {
    if (error) {
      return (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            background: "#fee2e2",
            color: "#991b1b",
            padding: "8px 12px",
            fontSize: 14,
            textAlign: "center",
            zIndex: 9998,
          }}
        >
          There was a problem: {error}
        </div>
      );
    }
    if (status === "pending_conversation") {
      return (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            background: "#fffbeb",
            color: "#92400e",
            padding: "8px 12px",
            fontSize: 14,
            textAlign: "center",
            zIndex: 9998,
          }}
        >
          This asset is being prepared. You can still start a chat below.
        </div>
      );
    }
    return null;
  };

  // Debug: surface env needed for public chat API when in dev
  useEffect(() => {
    if (import.meta.env.VITE_STAGE === "dev") {
      console.log("Public Chat Env:", {
        VITE_PUBLIC_CHAT_API_URL: import.meta.env.VITE_PUBLIC_CHAT_API_URL,
        VITE_PUBLIC_CHAT_API_KEY: import.meta.env.VITE_PUBLIC_CHAT_API_KEY
          ? "<set>"
          : "<missing>",
        VITE_PUBLIC_CHAT_WS_URL: import.meta.env.VITE_PUBLIC_CHAT_WS_URL,
        VITE_DEFAULT_TENANT_ID: import.meta.env.VITE_DEFAULT_TENANT_ID,
      });
    }
  }, []);

  // Prevent hydration mismatches by ensuring client-side rendering
  if (!isClient) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontFamily: "system-ui, -apple-system, sans-serif",
        }}
      >
        Loading chat...
      </div>
    );
  }

  return (
    <div>
      <Banner />
      <EmbeddableChatWidget
        conversationId={conversationId}
        assetId={assetId}
        trackingCode={tracking}
        startMessage={startMessage}
        prefillMessage={prefill}
        prefillOnFirstSession
        deviceType={device}
        embedded
        showDestinationIframe={true}
      />
    </div>
  );
}
